import { appendIcon, renderSearchableSelector } from "./components/searchable-dropdown-view.js";

(function () {
  "use strict";

  const GENERATED = window.__fishystuffGeneratedI18n || {};
  const CATALOGS = GENERATED.catalogs || {};
  const PAGE_MANIFEST = GENERATED.pageManifest || {};
  const CONFIG_BRAND = Symbol.for("fishystuff.language-config-contract.v1");
  const REPOSITORY_BRAND = Symbol.for(
    "fishystuff.language-preference-repository.v1",
  );
  const CACHE_BRAND = Symbol.for("fishystuff.data-language-catalog-cache.v1");
  const CONFIG = window.__fishystuffLanguageConfigV1;
  const preferenceRepository =
    window.__fishystuffLanguagePreferenceRepository;
  const dataLanguageCache = window.__fishystuffDataLanguageCatalogCache;
  if (!CONFIG || CONFIG[CONFIG_BRAND] !== 1) {
    throw new TypeError("Site language requires config contract v1.");
  }
  if (!preferenceRepository || preferenceRepository[REPOSITORY_BRAND] !== 1) {
    throw new TypeError("Site language requires preference repository v1.");
  }
  if (!dataLanguageCache || dataLanguageCache[CACHE_BRAND] !== 1) {
    throw new TypeError("Site language requires data-language cache v1.");
  }
  const AUTO_VALUE = CONFIG.autoValue;
  const FOLLOW_CONTENT_VALUE = CONFIG.followContentValue;
  const REGION_DEFAULT_VALUE = CONFIG.regionDefaultValue;
  const CHANGE_EVENT = "fishystuff:languagechange";
  const LANGUAGE_DETAILS_BOUND_ATTR = "data-language-refresh-bound";
  const SERVICE_REGION_ICON_ALIASES = Object.freeze({
    na: "service-region-na",
    eu: "service-region-eu",
    mena: "service-region-mena",
    ru: "service-region-ru",
    asia: "service-region-asia",
    jp: "service-region-jp",
    sa: "service-region-sa",
    kr: "service-region-kr",
    tw: "service-region-tw",
    gt: "service-region-gt",
    ceu: "service-region-ceu",
    cna: "service-region-cna",
    cas: "service-region-cas",
  });
  const LANGUAGE_ICON_ALIASES = Object.freeze({
    en: "language-en",
    "en-us": "language-en-us",
    de: "language-de",
    "de-de": "language-de",
    es: "language-es",
    sp: "language-es",
    fr: "language-fr",
    id: "language-id",
    jp: "language-ja",
    ja: "language-ja",
    kr: "language-ko",
    ko: "language-ko",
    "ko-kr": "language-ko",
    pt: "language-pt",
    ru: "language-ru",
    th: "language-th",
    tr: "language-tr",
    tr_en: "language-en",
    tw: "language-zh",
    zh: "language-zh",
  });
  let runtimeApiLanguages = initialApiLanguages();
  let dataLanguagesPromise = null;
  let dataLanguagesAbortController = null;
  let preferenceAuthorityPromise = null;
  let initialized = false;
  let disposed = false;
  let lastProjectedSnapshot = null;

  function trimString(value) {
    return String(value ?? "").trim();
  }

  function isPlainObject(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  }

  function canonicalServerDataspaceCode(value) {
    return CONFIG.canonicalServerDataspaceCode(value);
  }

  function normalizeApiLanguages(values) {
    return Array.from(CONFIG.normalizeDataLanguages(values));
  }

  function initialApiLanguages() {
    const cached = dataLanguageCache.state();
    return cached.kind === "ready"
      ? cached.languages.slice()
      : CONFIG.apiLanguages.slice();
  }

  function contentLanguages() {
    return CONFIG.contentLanguages.slice();
  }

  function localeLanguages() {
    return CONFIG.localeLanguages.slice();
  }

  function serverDataspaces() {
    return CONFIG.serverDataspaces.slice();
  }

  function serviceRegions() {
    return serverDataspaces();
  }

  function serverDataspaceByCode(code) {
    const normalizedCode = canonicalServerDataspaceCode(code);
    return serverDataspaces().find((dataspace) => dataspace.code === normalizedCode) || null;
  }

  function apiLanguages() {
    return runtimeApiLanguages.slice();
  }

  function defaultContentLang() {
    return CONFIG.defaultContentLang;
  }

  function defaultLocale() {
    return CONFIG.defaultLocale;
  }

  function defaultServerDataspace() {
    return CONFIG.defaultServerDataspace;
  }

  function defaultApiLangForServerDataspace(serverDataspaceCode) {
    const dataspace = serverDataspaceByCode(serverDataspaceCode);
    const dataspaceDefault = trimString(dataspace?.defaultApiLang);
    if (dataspaceDefault && runtimeApiLanguages.includes(dataspaceDefault)) {
      return dataspaceDefault;
    }
    for (const code of dataspace?.dataLanguages || []) {
      if (runtimeApiLanguages.includes(code)) {
        return code;
      }
    }
    const configured = trimString(CONFIG.defaultApiLang);
    if (configured && runtimeApiLanguages.includes(configured)) {
      return configured;
    }
    return runtimeApiLanguages[0] || configured || "en";
  }

  function dataLanguageForContent(contentLang) {
    const language = contentLanguages().find((candidate) => (
      trimString(candidate?.code) === trimString(contentLang)
    ));
    return trimString(language?.dataLanguage);
  }

  function defaultApiLangForContent(contentLang) {
    const dataLanguage = dataLanguageForContent(contentLang);
    return dataLanguage && runtimeApiLanguages.includes(dataLanguage) ? dataLanguage : "";
  }

  function currentContentLang() {
    const doc = document.documentElement;
    const explicit = trimString(doc.getAttribute("data-content-lang") || doc.lang);
    return explicit || defaultContentLang();
  }

  function defaultLocaleForContent(contentLang) {
    return localeLanguages().includes(contentLang) ? contentLang : defaultLocale();
  }

  function resolveLocale(contentLang, setting) {
    const normalizedSetting = trimString(setting);
    if (normalizedSetting && localeLanguages().includes(normalizedSetting)) {
      return normalizedSetting;
    }
    return defaultLocaleForContent(contentLang);
  }

  function resolveServerDataspace(setting) {
    const normalizedSetting = canonicalServerDataspaceCode(setting);
    if (normalizedSetting && serverDataspaceByCode(normalizedSetting)) {
      return normalizedSetting;
    }
    return defaultServerDataspace();
  }

  function resolveApiLang(contentLang, setting, serverDataspace) {
    const normalizedSetting = trimString(setting);
    if (normalizedSetting && apiLanguages().includes(normalizedSetting)) {
      return normalizedSetting;
    }
    if (normalizedSetting === REGION_DEFAULT_VALUE) {
      return defaultApiLangForServerDataspace(serverDataspace);
    }
    const contentDefault = defaultApiLangForContent(contentLang);
    if (contentDefault) {
      return contentDefault;
    }
    return defaultApiLangForServerDataspace(serverDataspace);
  }

  function shouldLoadDataLanguages(state = snapshot()) {
    const setting = trimString(state.apiLangSetting);
    if (
      setting
      && CONFIG.isDataLanguage(setting)
      && !apiLanguages().includes(setting)
    ) {
      return true;
    }
    if (!setting) {
      const contentDefault = dataLanguageForContent(state.contentLang);
      if (contentDefault && !apiLanguages().includes(contentDefault)) {
        return true;
      }
    }
    if (setting !== REGION_DEFAULT_VALUE) {
      return false;
    }
    const regionDefault = trimString(serverDataspaceByCode(state.serviceRegion)?.defaultApiLang);
    return Boolean(
      regionDefault
      && CONFIG.isDataLanguage(regionDefault)
      && !apiLanguages().includes(regionDefault),
    );
  }

  function replaceVars(message, vars = {}) {
    return String(message || "").replace(/\{\s*\$([A-Za-z0-9_]+)\s*\}/g, function (_match, name) {
      return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : "";
    });
  }

  function snapshot() {
    const contentLang = currentContentLang();
    const preference = preferenceRepository.decision();
    const localeSetting = trimString(preference.locale);
    const locale = resolveLocale(contentLang, localeSetting);
    const serviceRegionSetting = trimString(preference.serverDataspace);
    const serverDataspace = resolveServerDataspace(serviceRegionSetting);
    const apiLangSetting = trimString(preference.dataLanguage);
    const apiLang = resolveApiLang(contentLang, apiLangSetting, serverDataspace);
    return Object.freeze({
      contentLang,
      locale,
      serverDataspace,
      serviceRegion: serverDataspace,
      apiLang,
      localeSetting,
      serverDataspaceSetting: serviceRegionSetting,
      serviceRegionSetting,
      apiLangSetting,
      preferenceKind: preference.repositoryKind,
      preferenceRevision: preference.revision,
      canChangePreferences: preference.canChange,
      localeSource: localeSetting ? "explicit" : "auto",
      serviceRegionSource: serviceRegionSetting ? "explicit" : "auto",
      apiLangSource: apiLangSetting === REGION_DEFAULT_VALUE
        ? "service-region"
        : apiLangSetting ? "explicit" : "content",
    });
  }

  function sameSnapshot(left, right) {
    return Boolean(left && right)
      && left.contentLang === right.contentLang
      && left.locale === right.locale
      && left.serverDataspace === right.serverDataspace
      && left.serviceRegion === right.serviceRegion
      && left.apiLang === right.apiLang
      && left.localeSetting === right.localeSetting
      && left.serverDataspaceSetting === right.serverDataspaceSetting
      && left.serviceRegionSetting === right.serviceRegionSetting
      && left.apiLangSetting === right.apiLangSetting
      && left.preferenceKind === right.preferenceKind
      && left.preferenceRevision === right.preferenceRevision
      && left.canChangePreferences === right.canChangePreferences;
  }

  function apiUrl(pathname) {
    if (typeof window.__fishystuffResolveApiUrl === "function") {
      return window.__fishystuffResolveApiUrl(pathname);
    }
    const configured = window.__fishystuffRuntimeConfig || {};
    const baseUrl = trimString(window.__fishystuffApiBaseUrl || configured.apiBaseUrl);
    if (!baseUrl) {
      return pathname;
    }
    try {
      return new URL(pathname, baseUrl).toString();
    } catch (_error) {
      return pathname;
    }
  }

  function updateRuntimeApiLanguages(languages) {
    const normalized = normalizeApiLanguages(languages);
    if (!normalized.length) {
      return false;
    }
    if (normalized.join("\n") === runtimeApiLanguages.join("\n")) {
      return false;
    }
    runtimeApiLanguages = normalized;
    return true;
  }

  function projectRuntimeApiLanguages(languages) {
    const before = snapshot();
    if (!updateRuntimeApiLanguages(languages)) return false;
    const after = snapshot();
    if (initialized && !disposed) {
      apply(document);
      renderServiceRegionPanels(document);
      renderLanguagePanels(document);
      if (!sameSnapshot(before, after)) dispatchChange(after);
      lastProjectedSnapshot = after;
    }
    return true;
  }

  async function loadDataLanguages() {
    if (dataLanguagesPromise) {
      return dataLanguagesPromise;
    }
    dataLanguagesPromise = (async () => {
      if (typeof fetch !== "function" || disposed) {
        return apiLanguages();
      }
      dataLanguagesAbortController = typeof AbortController === "function"
        ? new AbortController()
        : null;
      const signal = dataLanguagesAbortController?.signal;
      let response;
      try {
        response = await fetch(apiUrl("/api/v1/meta"), signal ? { signal } : undefined);
      } catch (_error) {
        return apiLanguages();
      }
      if (disposed || signal?.aborted) return apiLanguages();
      if (!response || !response.ok || typeof response.json !== "function") {
        return apiLanguages();
      }
      let meta;
      try {
        meta = await response.json();
      } catch (_error) {
        return apiLanguages();
      }
      if (disposed || signal?.aborted) return apiLanguages();
      const normalized = normalizeApiLanguages(meta && meta.data_languages);
      if (!normalized.length) return apiLanguages();
      const saved = await dataLanguageCache.replace(normalized);
      if (!disposed && !signal?.aborted && saved.kind === "ready") {
        projectRuntimeApiLanguages(saved.languages);
      }
      return apiLanguages();
    })();
    return dataLanguagesPromise;
  }

  function languageReady() {
    preferenceAuthorityPromise ??= Promise.resolve()
      .then(() => preferenceRepository.load())
      .catch(() => preferenceRepository.state());
    return preferenceAuthorityPromise.then(() => {
      const current = snapshot();
      if (!shouldLoadDataLanguages(current)) return current;
      return loadDataLanguages()
        .catch(() => apiLanguages())
        .then(() => snapshot());
    });
  }

  function catalogForLocale(locale) {
    return isPlainObject(CATALOGS[locale]) ? CATALOGS[locale] : {};
  }

  function t(key, vars = {}, options = {}) {
    const requestedLocale = trimString(options.locale) || snapshot().locale;
    const preferred = catalogForLocale(requestedLocale);
    const fallback = catalogForLocale(defaultLocale());
    const raw = preferred[key] ?? fallback[key];
    if (raw === undefined) {
      return key;
    }
    return replaceVars(raw, vars);
  }

  function rootPathForContentLang(code) {
    const match = contentLanguages().find((language) => trimString(language.code) === trimString(code));
    if (!match) {
      return "/";
    }
    const prefix = trimString(match.pathPrefix || "/");
    return prefix === "/" ? "/" : `${prefix.replace(/\/+$/, "")}/`;
  }

  function normalizeRouteKey(pathname) {
    let normalized = trimString(pathname || "/");
    if (!normalized.startsWith("/")) {
      normalized = `/${normalized}`;
    }
    normalized = normalized.replace(/\/{2,}/g, "/");
    if (normalized !== "/" && !normalized.endsWith("/")) {
      normalized = `${normalized}/`;
    }
    return normalized;
  }

  function routeState(pathname) {
    const normalizedPath = normalizeRouteKey(pathname);
    const prefixed = contentLanguages()
      .filter((language) => trimString(language.pathPrefix) && trimString(language.pathPrefix) !== "/")
      .slice()
      .sort((left, right) => trimString(right.pathPrefix).length - trimString(left.pathPrefix).length);
    for (const language of prefixed) {
      const prefix = trimString(language.pathPrefix).replace(/\/+$/, "");
      if (normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)) {
        const relative = normalizedPath.slice(prefix.length) || "/";
        return {
          contentLang: trimString(language.code),
          routeKey: normalizeRouteKey(relative),
        };
      }
    }
    return {
      contentLang: defaultContentLang(),
      routeKey: normalizedPath,
    };
  }

  function resolveContentUrl(targetContentLang, pathname = window.location.pathname) {
    const route = routeState(pathname);
    const variants = PAGE_MANIFEST[route.routeKey];
    if (variants && variants[targetContentLang]) {
      return variants[targetContentLang];
    }
    if (variants && variants[defaultContentLang()]) {
      return variants[defaultContentLang()];
    }
    return rootPathForContentLang(targetContentLang);
  }

  function dispatchChange(detail) {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
  }

  function apply(root = document) {
    if (!root || typeof root.querySelectorAll !== "function") {
      return;
    }
    for (const element of root.querySelectorAll("[data-i18n-text]")) {
      const nextText = t(trimString(element.getAttribute("data-i18n-text")));
      if (element.textContent !== nextText) {
        element.textContent = nextText;
      }
    }
    for (const element of root.querySelectorAll("*")) {
      if (!element.attributes) {
        continue;
      }
      for (const attribute of Array.from(element.attributes)) {
        if (!attribute.name.startsWith("data-i18n-attr-")) {
          continue;
        }
        const targetName = attribute.name.slice("data-i18n-attr-".length);
        const nextValue = t(trimString(attribute.value));
        if (element.getAttribute?.(targetName) !== nextValue) {
          element.setAttribute(targetName, nextValue);
        }
      }
    }
  }

  function translatedOptionLabel(prefix, code, options = {}) {
    const normalizedCode = trimString(code);
    if (!normalizedCode) {
      return "";
    }
    const key = `${prefix}.${normalizedCode}`;
    const translated = t(key, {}, options);
    return translated === key ? normalizedCode : translated;
  }

  function localeLanguageLabel(code) {
    const normalizedCode = trimString(code);
    return translatedOptionLabel("language.option.locale", normalizedCode, {
      locale: normalizedCode,
    });
  }

  function localeLanguageOptions() {
    return localeLanguages().map((code) => ({
      value: code,
      label: localeLanguageLabel(code),
    }));
  }

  function serviceRegionLabel(code) {
    return translatedOptionLabel("language.option.service_region", code);
  }

  function serviceRegionOptionLabel(code) {
    const normalizedCode = trimString(code);
    const acronym = normalizedCode.toUpperCase();
    const label = serviceRegionLabel(normalizedCode);
    const suffix = `(${acronym})`;
    if (!acronym || !label) {
      return label || acronym;
    }
    if (label.toUpperCase().endsWith(suffix)) {
      const name = label.slice(0, -suffix.length).trim();
      return name ? `${acronym} - ${name}` : acronym;
    }
    return label.toUpperCase() === acronym ? acronym : `${acronym} - ${label}`;
  }

  function serviceRegionCompactLabel(code) {
    return trimString(code).toUpperCase();
  }

  function dataLanguageLabel(code) {
    return translatedOptionLabel("language.option.api", code);
  }

  function orderedApiLanguagesForServerDataspace(serverDataspaceCode) {
    const available = apiLanguages();
    const regionLanguages = serverDataspaceByCode(serverDataspaceCode)?.dataLanguages || [];
    const ordered = [];
    const seen = new Set();
    for (const code of regionLanguages) {
      if (available.includes(code) && !seen.has(code)) {
        seen.add(code);
        ordered.push(code);
      }
    }
    for (const code of available) {
      if (!seen.has(code)) {
        seen.add(code);
        ordered.push(code);
      }
    }
    return ordered;
  }

  function dataLanguageOptions(serviceRegionCode = snapshot().serviceRegion) {
    return orderedApiLanguagesForServerDataspace(serviceRegionCode).map((code) => ({
      value: code,
      label: dataLanguageLabel(code),
    }));
  }

  function spriteHref(alias) {
    const normalizedAlias = trimString(alias);
    return normalizedAlias ? `#fishy-${normalizedAlias}` : "";
  }

  function serviceRegionIconHref(code) {
    return spriteHref(SERVICE_REGION_ICON_ALIASES[trimString(code).toLowerCase()] || "globe-2-fill");
  }

  function languageIconHref(code) {
    const normalizedCode = trimString(code).toLowerCase();
    if (!normalizedCode) {
      return "";
    }
    const dashedCode = normalizedCode.replace(/_/g, "-");
    const shortCode = dashedCode.split("-")[0];
    return spriteHref(
      LANGUAGE_ICON_ALIASES[normalizedCode]
        || LANGUAGE_ICON_ALIASES[dashedCode]
        || LANGUAGE_ICON_ALIASES[shortCode]
        || "globe-2-fill",
    );
  }

  function setInlineLabel(element, label, iconHref = "") {
    if (!element || typeof element.replaceChildren !== "function") {
      if (element) {
        element.textContent = label;
      }
      return;
    }
    element.replaceChildren();
    element.classList?.add("inline-flex", "items-center", "gap-1.5");
    appendIcon(element, iconHref, "fishy-icon size-3.5 shrink-0");
    const labelText = document.createElement("span");
    labelText.className = "min-w-0 truncate";
    labelText.textContent = label;
    element.appendChild(labelText);
  }

  function renderOptionField({
    id,
    label,
    help,
    value,
    options,
    onSelect,
    disabled = false,
    iconHref = "",
  }) {
    const fieldset = document.createElement("fieldset");
    fieldset.className = "fieldset min-w-0 max-w-full rounded-box border border-base-300 bg-base-100 p-3";

    const legend = document.createElement("legend");
    legend.className = "fieldset-legend flex items-center gap-2 text-sm";
    appendIcon(legend, iconHref);
    const labelText = document.createElement("span");
    labelText.textContent = label;
    legend.appendChild(labelText);
    fieldset.appendChild(legend);

    const selector = renderSearchableSelector({
      id,
      label,
      value,
      options,
      onSelect,
      disabled,
      placeholder: t("common.action.search"),
    });
    selector.setAttribute("data-language-preference-writable", String(!disabled));
    fieldset.appendChild(selector);

    const note = document.createElement("p");
    note.className = "mt-2 text-xs text-base-content/70";
    note.textContent = help;
    fieldset.appendChild(note);

    return fieldset;
  }

  function updateLanguageLabels(root, state) {
    const current = state || snapshot();
    const localeLabel = localeLanguageLabel(current.locale);
    const contentLabel = translatedOptionLabel("language.option.content", current.contentLang);
    const serviceLabel = serviceRegionLabel(current.serviceRegion);
    const serviceCompactLabel = serviceRegionCompactLabel(current.serviceRegion);
    const apiLabel = dataLanguageLabel(current.apiLang);
    for (const element of root.querySelectorAll("[data-language-current-label]")) {
      setInlineLabel(element, localeLabel, languageIconHref(current.locale));
    }
    for (const element of root.querySelectorAll("[data-language-content-label]")) {
      setInlineLabel(element, contentLabel, languageIconHref(current.contentLang));
    }
    for (const element of root.querySelectorAll("[data-language-service-region-label]")) {
      setInlineLabel(element, serviceLabel, serviceRegionIconHref(current.serviceRegion));
    }
    for (const element of root.querySelectorAll("[data-language-service-region-compact-label]")) {
      setInlineLabel(element, serviceCompactLabel, serviceRegionIconHref(current.serviceRegion));
    }
    for (const element of root.querySelectorAll("[data-language-api-label]")) {
      setInlineLabel(element, apiLabel, languageIconHref(current.apiLang));
    }
  }

  function languagePanels(root = document) {
    const panels = Array.from(root.querySelectorAll("[data-language-panel]"))
      .filter((panel) => panel instanceof HTMLElement);
    return panels;
  }

  function serviceRegionPanels(root = document) {
    const panels = Array.from(root.querySelectorAll("[data-service-region-panel]"))
      .filter((panel) => panel instanceof HTMLElement);
    return panels;
  }

  function panelDetails(panel) {
    return typeof panel.closest === "function" ? panel.closest("details") : null;
  }

  function isVisibleLanguagePanel(panel) {
    const details = panelDetails(panel);
    return !details || Boolean(details.open);
  }

  function bindLanguagePanelRefresh(panels) {
    for (const panel of panels) {
      const details = panelDetails(panel);
      if (!details || details.hasAttribute(LANGUAGE_DETAILS_BOUND_ATTR)) {
        continue;
      }
      details.setAttribute(LANGUAGE_DETAILS_BOUND_ATTR, "true");
      details.addEventListener("toggle", () => {
        if (!details.open) {
          return;
        }
        loadDataLanguages()
          .then(() => renderLanguagePanels(document))
          .catch(() => {});
      });
    }
  }

  function maybeLoadDataLanguagesForVisiblePanels(panels) {
    if (dataLanguagesPromise || !panels.some(isVisibleLanguagePanel)) {
      return;
    }
    loadDataLanguages().catch(() => {});
  }

  function preferenceCommandOptions() {
    const state = preferenceRepository.state();
    if (state.kind === "ready") {
      return { expectedRevision: state.revision };
    }
    if (state.kind === "missing") {
      return { expectedRevision: null };
    }
    return {};
  }

  function reportPreferenceFailure(state) {
    const message = state?.kind === "conflict"
      ? "Language preferences changed in another tab. Review the latest values and try again."
      : "Language preferences could not be saved. Reset the stored record or try again.";
    window.__fishystuffToast?.error?.(message);
  }

  async function runPreferenceCommand(command) {
    const acknowledged = await command(preferenceCommandOptions());
    if (acknowledged?.kind === "ready" || acknowledged?.kind === "missing") {
      window.location.reload();
      return acknowledged;
    }
    renderServiceRegionPanels(document);
    renderLanguagePanels(document);
    reportPreferenceFailure(acknowledged);
    return acknowledged;
  }

  function renderServiceRegionPanels(root = document) {
    const panels = serviceRegionPanels(root);
    if (!panels.length) {
      return;
    }
    const state = snapshot();
    panels.forEach((panel, index) => {
      const instanceId = trimString(panel.getAttribute("data-service-region-panel-id")) || String(index + 1);
      const compact = panel.hasAttribute("data-service-region-compact");
      panel.replaceChildren();
      const field = (compact ? renderSearchableSelector : renderOptionField)({
        id: `service-region-options-${instanceId}`,
        label: t("language.menu.service_region"),
        help: t("language.menu.service_region_help"),
        value: state.serviceRegion,
        compact,
        placeholder: t("common.action.search"),
        options: serverDataspaces().map((dataspace) => ({
          value: dataspace.code,
          label: serviceRegionOptionLabel(dataspace.code),
          compactLabel: dataspace.code.toUpperCase(),
          searchText: serviceRegionLabel(dataspace.code),
          iconHref: serviceRegionIconHref(dataspace.code),
        })),
        disabled: !state.canChangePreferences,
        async onSelect(value) {
          const next = trimString(value);
          await runPreferenceCommand((options) => (
            preferenceRepository.setServerDataspace(
              next === defaultServerDataspace() ? null : next,
              options,
            )
          ));
        },
      });
      if (compact) {
        field.setAttribute("data-language-preference-writable", String(state.canChangePreferences));
      }
      panel.appendChild(field);
    });
    updateLanguageLabels(root, state);
  }

  function renderLanguagePanels(root = document) {
    const panels = languagePanels(root);
    if (!panels.length) {
      return;
    }
    bindLanguagePanelRefresh(panels);
    const state = snapshot();
    panels.forEach((panel, index) => {
      const instanceId = trimString(panel.getAttribute("data-language-panel-id")) || String(index + 1);
      panel.replaceChildren();

      panel.appendChild(renderOptionField({
        id: `content-language-options-${instanceId}`,
        iconHref: "#fishy-nav-guides",
        label: t("language.menu.content"),
        help: t("language.menu.content_help"),
        value: state.contentLang,
        options: contentLanguages().map((language) => ({
          value: trimString(language.code),
          label: translatedOptionLabel("language.option.content", language.code),
          iconHref: languageIconHref(language.code),
        })),
        onSelect(value) {
          const next = trimString(value);
          if (next && next !== state.contentLang) {
            window.location.assign(resolveContentUrl(next));
          }
        },
      }));

      panel.appendChild(renderOptionField({
        id: `locale-language-options-${instanceId}`,
        iconHref: "#fishy-web-fill",
        label: t("language.menu.locale"),
        help: t("language.menu.locale_help"),
        value: state.localeSetting || AUTO_VALUE,
        options: [
          {
            value: AUTO_VALUE,
            label: t("language.option.auto.locale"),
            title: t("language.option.auto.locale"),
            iconHref: "#fishy-web-fill",
          },
          ...localeLanguageOptions().map((option) => ({
            ...option,
            iconHref: languageIconHref(option.value),
          })),
        ],
        disabled: !state.canChangePreferences,
        async onSelect(value) {
          const next = trimString(value);
          await runPreferenceCommand((options) => (
            preferenceRepository.setLocale(
              next === AUTO_VALUE ? null : next,
              options,
            )
          ));
        },
      }));

      panel.appendChild(renderOptionField({
        id: `api-language-options-${instanceId}`,
        iconHref: "#fishy-server-2-fill",
        label: t("language.menu.api"),
        help: t("language.menu.api_help"),
        value: state.apiLangSetting || FOLLOW_CONTENT_VALUE,
        options: [
          {
            value: FOLLOW_CONTENT_VALUE,
            label: t("language.option.follow_content.api"),
            title: t("language.option.follow_content.api"),
            iconHref: "#fishy-web-fill",
          },
          {
            value: REGION_DEFAULT_VALUE,
            label: t("language.option.region_default.api"),
            title: t("language.option.region_default.api"),
            iconHref: "#fishy-server-2-fill",
          },
          ...dataLanguageOptions(state.serviceRegion).map((option) => ({
            ...option,
            iconHref: languageIconHref(option.value),
          })),
        ],
        disabled: !state.canChangePreferences,
        async onSelect(value) {
          const next = trimString(value);
          await runPreferenceCommand((options) => (
            preferenceRepository.setDataLanguage(
              next === FOLLOW_CONTENT_VALUE ? null : next,
              options,
            )
          ));
        },
        gridClass: "grid-cols-1",
      }));
    });

    updateLanguageLabels(root, state);
    maybeLoadDataLanguagesForVisiblePanels(panels);
  }

  function init() {
    if (disposed || initialized) return;
    initialized = true;
    const current = snapshot();
    lastProjectedSnapshot = current;
    apply(document);
    renderServiceRegionPanels(document);
    renderLanguagePanels(document);
    dispatchChange(current);
  }

  function projectPreferenceRevision() {
    if (!initialized || disposed) return;
    const current = snapshot();
    apply(document);
    renderServiceRegionPanels(document);
    renderLanguagePanels(document);
    if (!sameSnapshot(lastProjectedSnapshot, current)) {
      lastProjectedSnapshot = current;
      dispatchChange(current);
    }
  }

  const unsubscribePreferences = preferenceRepository.subscribe(() => {
    projectPreferenceRevision();
  });
  const unsubscribeDataLanguages = dataLanguageCache.subscribe((state) => {
    if (state.kind === "ready") {
      projectRuntimeApiLanguages(state.languages);
    }
  });

  function handlePageHide(event) {
    if (event?.persisted === true || disposed) return;
    disposed = true;
    unsubscribePreferences();
    unsubscribeDataLanguages();
    dataLanguagesAbortController?.abort();
    dataLanguagesAbortController = null;
    preferenceRepository.dispose();
    dataLanguageCache.dispose();
    window.removeEventListener?.("pagehide", handlePageHide);
    document.removeEventListener?.("DOMContentLoaded", init);
  }

  window.__fishystuffLanguage = Object.freeze({
    autoValue: AUTO_VALUE,
    event: CHANGE_EVENT,
    apply,
    current: snapshot,
    dataLanguages: apiLanguages,
    dataLanguageOptions,
    localeLanguageOptions,
    renderPanels: renderLanguagePanels,
    renderServiceRegionPanels,
    resolveContentUrl,
    serverDataspaces,
    serviceRegions,
    ready: languageReady(),
    refreshDataLanguages: loadDataLanguages,
    t,
  });

  document.addEventListener("DOMContentLoaded", init);
  window.addEventListener("pagehide", handlePageHide);
})();
